import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { createClient } from '@supabase/supabase-js';
import { TennisBall, Eye, EyeSlash, ArrowLeft } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { tokenStore } from '../lib/api';

// Minimal Supabase client used ONLY for the password-recovery exchange.
// The rest of the app talks through our FastAPI backend, but Supabase
// recovery tokens arrive as a URL hash and must be verified client-side.
//
// IMPORTANT: we deliberately use the DEFAULT (implicit) flow — not PKCE —
// because:
//  1. Supabase's stock recovery email template ships an implicit-flow link
//     (`#access_token=...&type=recovery`). PKCE would silently ignore that.
//  2. Users frequently open the email on a different device than the one
//     they requested the reset from; PKCE requires a `code_verifier` stored
//     at request-time, which is missing cross-device.
// A separate `storageKey` keeps the recovery session isolated from the
// rest of the app's auth state.
const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL;
const SUPABASE_ANON_KEY = process.env.REACT_APP_SUPABASE_ANON_KEY;
const supabase =
  SUPABASE_URL && SUPABASE_ANON_KEY
    ? createClient(SUPABASE_URL, SUPABASE_ANON_KEY, {
        auth: {
          detectSessionInUrl: true,
          persistSession: false,
          autoRefreshToken: false,
          storageKey: 'tl-recovery',
        },
      })
    : null;

// Parse Supabase's hash payload (`#k1=v1&k2=v2&...`) into a plain object.
// Used to surface error messages and to detect implicit-flow tokens.
function parseHashParams(hash) {
  const out = {};
  if (!hash) return out;
  const clean = hash.startsWith('#') ? hash.slice(1) : hash;
  for (const part of clean.split('&')) {
    if (!part) continue;
    const [k, v] = part.split('=');
    if (k) out[decodeURIComponent(k)] = decodeURIComponent(v || '');
  }
  return out;
}

export default function ResetPasswordPage() {
  const [ready, setReady] = useState(false);
  const [pw1, setPw1] = useState('');
  const [pw2, setPw2] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [err, setErr] = useState('');
  const [loading, setLoading] = useState(false);
  const [configError, setConfigError] = useState(false);
  const [linkError, setLinkError] = useState(''); // friendly error for expired/invalid links
  const navigate = useNavigate();
  const { logout } = useAuth();
  const fallbackTimer = useRef(null);

  useEffect(() => {
    if (!supabase) {
      setConfigError(true);
      return;
    }

    // 1. Supabase sometimes returns the error directly in the hash when the
    //    recovery link is invalid/expired (e.g.
    //    `#error=access_denied&error_code=otp_expired&error_description=...`).
    //    Surface a friendly message instead of letting the user stare at a
    //    spinner forever.
    const params = parseHashParams(window.location.hash);
    if (params.error || params.error_code || params.error_description) {
      const desc = params.error_description || params.error || 'This reset link is invalid or has expired.';
      setLinkError(desc.replace(/\+/g, ' '));
      return;
    }

    // 2. Listen for the recovery session being established (implicit flow
    //    auto-detected by the client because `detectSessionInUrl: true`).
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
      if (event === 'PASSWORD_RECOVERY' || event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED') {
        setReady(true);
        if (fallbackTimer.current) clearTimeout(fallbackTimer.current);
      }
    });

    // 3. Also consider an already-exchanged session (e.g. fast re-renders).
    supabase.auth.getSession().then(({ data }) => {
      if (data.session) {
        setReady(true);
        if (fallbackTimer.current) clearTimeout(fallbackTimer.current);
      }
    });

    // 4. Hard fallback — if neither the event nor an existing session shows
    //    up within 6s, the link is almost certainly malformed/expired.
    fallbackTimer.current = setTimeout(() => {
      setReady((current) => {
        if (!current) {
          setLinkError('This reset link is invalid or has expired. Request a new one to continue.');
        }
        return current;
      });
    }, 6000);

    return () => {
      subscription.unsubscribe();
      if (fallbackTimer.current) clearTimeout(fallbackTimer.current);
    };
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErr('');
    if (!pw1 || pw1.length < 6) {
      setErr('Password must be at least 6 characters.');
      return;
    }
    if (pw1 !== pw2) {
      setErr('Passwords do not match.');
      return;
    }
    setLoading(true);
    try {
      const { error } = await supabase.auth.updateUser({ password: pw1 });
      if (error) throw error;
      // Fully end every session before sending the user back to /login.
      //
      // (1) The isolated Supabase recovery client — drops the temporary
      //     access/refresh tokens so a refreshed page can't replay them.
      // (2) The MAIN app session — if the user still had a stale
      //     `tl_session_token` from a previous device sign-in (very common
      //     among forgot-password users), AuthContext would happily route
      //     them through the invite/onboarding guards on /login. We must
      //     clear it, blow away AuthContext state, AND remove any leftover
      //     Supabase localStorage entries created during the recovery hop.
      try { await supabase.auth.signOut(); } catch { /* ignore */ }
      try {
        Object.keys(window.localStorage).forEach((k) => {
          if (k.startsWith('tl-recovery') || k.startsWith('sb-') && k.includes('-auth-token')) {
            window.localStorage.removeItem(k);
          }
        });
      } catch { /* ignore storage errors */ }
      tokenStore.clear();
      try { await logout(); } catch { /* ignore — best effort */ }

      toast.success('Password updated. Please sign in with your new password.');
      // `state.passwordResetSuccess` tells LoginPage to bypass its
      // auto-redirect guards for this single render and show the success
      // banner. Without this, an authed-but-no-player user lands on
      // /redeem-invite — the exact bug the user reported.
      navigate('/login', { replace: true, state: { passwordResetSuccess: true } });
    } catch (ex) {
      setErr(ex?.message || 'Failed to update password. The reset link may have expired.');
    } finally {
      setLoading(false);
    }
  };

  if (configError) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6 bg-[#fbfaf8]">
        <div className="max-w-sm text-center space-y-4" data-testid="reset-config-error">
          <h1 className="font-serif text-2xl text-[#013523]">Reset unavailable</h1>
          <p className="text-sm text-[#6a766e]">Password reset isn't configured in this environment.</p>
          <Link to="/login" className="text-sm text-[#015e3f] underline">Back to sign in</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-[#fbfaf8]">
      <div className="absolute inset-0 h-[45vh]">
        <img
          src="https://images.unsplash.com/photo-1719760825481-90f57e905ccb?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njl8MHwxfHNlYXJjaHwyfHx0ZW5uaXMlMjBjb3VydCUyMGdyYXNzfGVufDB8fHx8MTc3NjIwMzAzOHww&ixlib=rb-4.1.0&q=85"
          alt=""
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/20 to-[#fbfaf8]" />
      </div>

      <div className="relative z-10 flex-1 flex flex-col items-center justify-end pb-12 px-6" style={{ minHeight: '100vh' }}>
        <div className="mb-auto pt-16 text-center">
          <div className="inline-flex items-center gap-2">
            <TennisBall size={28} weight="duotone" className="text-white" />
            <span className="font-serif text-2xl text-white tracking-tight">The Rippers Club</span>
          </div>
        </div>

        <div className="w-full max-w-sm bg-[#fbfaf8] rounded-2xl border border-[#e3dcd1] shadow-xl p-6 space-y-5" data-testid="reset-password-page">
          <div className="text-center">
            <h1 className="font-serif text-3xl text-[#013523] tracking-tight">Reset password</h1>
            {!ready && !linkError && (
              <p className="text-xs text-[#6a766e] mt-2" data-testid="reset-validating">
                Validating reset link…
              </p>
            )}
          </div>

          {linkError ? (
            <div className="space-y-4" data-testid="reset-link-error">
              <div className="bg-red-50 text-red-700 text-sm rounded-xl px-4 py-3 border border-red-200">
                {linkError}
              </div>
              <button
                type="button"
                data-testid="reset-request-new-link"
                onClick={() => navigate('/forgot-password')}
                className="w-full bg-[#015e3f] text-white rounded-full px-6 py-3 font-medium transition-transform active:scale-95 hover:bg-[#014d34]"
              >
                Request a new link
              </button>
              <Link
                to="/login"
                className="flex items-center justify-center gap-1 text-sm text-[#6a766e] hover:text-[#013523]"
                data-testid="reset-back-to-login"
              >
                <ArrowLeft size={14} weight="bold" /> Back to sign in
              </Link>
            </div>
          ) : (
            <>
              {err && (
                <div
                  className="bg-red-50 text-red-600 text-sm rounded-xl px-4 py-3 border border-red-200"
                  data-testid="reset-error"
                >
                  {err}
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="text-xs font-sans tracking-[0.1em] uppercase text-[#6a766e] mb-1.5 block">
                    New password
                  </label>
                  <div className="relative">
                    <input
                      data-testid="reset-password-input"
                      type={showPw ? 'text' : 'password'}
                      value={pw1}
                      onChange={(e) => setPw1(e.target.value)}
                      className="w-full bg-white border border-[#e3dcd1] rounded-xl px-4 py-3 pr-12 text-[#2a332c] focus:outline-none focus:ring-2 focus:ring-[#015e3f]/20 focus:border-[#015e3f]"
                      placeholder="At least 6 characters"
                      minLength={6}
                      autoComplete="new-password"
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowPw(!showPw)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-[#6a766e]"
                      aria-label={showPw ? 'Hide password' : 'Show password'}
                    >
                      {showPw ? <EyeSlash size={20} /> : <Eye size={20} />}
                    </button>
                  </div>
                </div>
                <div>
                  <label className="text-xs font-sans tracking-[0.1em] uppercase text-[#6a766e] mb-1.5 block">
                    Confirm password
                  </label>
                  <input
                    data-testid="reset-password-confirm"
                    type={showPw ? 'text' : 'password'}
                    value={pw2}
                    onChange={(e) => setPw2(e.target.value)}
                    className="w-full bg-white border border-[#e3dcd1] rounded-xl px-4 py-3 text-[#2a332c] focus:outline-none focus:ring-2 focus:ring-[#015e3f]/20 focus:border-[#015e3f]"
                    placeholder="Re-enter new password"
                    autoComplete="new-password"
                    required
                  />
                </div>
                <button
                  data-testid="reset-submit"
                  type="submit"
                  disabled={loading || !ready}
                  className="w-full bg-[#015e3f] text-white rounded-full px-6 py-3 font-medium transition-transform active:scale-95 hover:bg-[#014d34] disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? 'Updating…' : 'Update password'}
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
